import React, { useMemo, useState } from 'react';
import {
  ICdwObjectsContainerFieldsFragment,
  ICdwTableFieldsFragment,
  Order,
} from '../../../../utils/apollo/graphqlSchema';
import { CdwObjectRowItem } from './SelectCdwTableFormUI';
import { SvgIcon } from '../../../../components/Icon';
import {
  ClickableStyledFormUIRow,
  StyledFormUIRow,
} from '../DataModelerCommonUI';
import SortableHeaderLabel from '../../../pageForm/SortableHeaderLabel';
import * as FormUI from '../../../pageForm/FormUI';

interface IProps {
  searchQuery: string;
  container: ICdwObjectsContainerFieldsFragment;
  openContainer: (name: string) => void;
  currentTable: ICdwTableFieldsFragment | null;
  onSelectTable: (selected: ICdwTableFieldsFragment | null) => void;
}

const CdwTableContainer = ({
  searchQuery,
  container,
  currentTable,
  onSelectTable,
  openContainer,
}: IProps) => {
  const { containers, tables } = container;
  const [sortOrder, setSortOrder] = useState(Order.Asc);
  const [hoveredItem, setHoveredItem] = useState('');

  const toggleSortOrder = () =>
    setSortOrder(sortOrder === Order.Asc ? Order.Desc : Order.Asc);

  const sortedAndFilteredContainers = useMemo(() => {
    const lcTerm = searchQuery.toLowerCase().trim();
    return containers
      .filter(
        (cont) => lcTerm === '' || cont.name.toLowerCase().includes(lcTerm),
      )
      .sort((a, b) =>
        sortOrder === Order.Desc
          ? b.name.localeCompare(a.name)
          : a.name.localeCompare(b.name),
      );
  }, [containers, searchQuery, sortOrder]);

  const sortedAndFilteredTables = useMemo(() => {
    const lcTerm = searchQuery.toLowerCase().trim();
    return tables
      .filter(
        (table) =>
          lcTerm === '' || table.shortName.toLowerCase().includes(lcTerm),
      )
      .sort((a, b) =>
        sortOrder === Order.Desc
          ? b.shortName.localeCompare(a.shortName)
          : a.shortName.localeCompare(b.shortName),
      );
  }, [searchQuery, tables, sortOrder]);

  const headerText =
    tables.length > 0
      ? 'Tables'
      : containers.length > 0
        ? `${containers[0].displayType}s`
        : 'Warehouse Objects';
  return (
    <FormUI.Container>
      <StyledFormUIRow $isHeader $gridTemplateColumns={'100%'}>
        <SortableHeaderLabel
          onClick={toggleSortOrder}
          activeSortType={sortOrder}
          text={headerText}
          isActive={true}
          shouldAlignRight={false}
        />
      </StyledFormUIRow>
      {sortedAndFilteredContainers.map((pathComponent) => {
        return (
          <ClickableStyledFormUIRow
            $gridTemplateColumns={'100%'}
            key={pathComponent.name}
            onClick={() => openContainer(pathComponent.name)}
            onMouseEnter={() => setHoveredItem(pathComponent.name)}
            onMouseLeave={() => setHoveredItem('')}
          >
            <CdwObjectRowItem>
              <SvgIcon
                width={14}
                height={12}
                name={'folder'}
                isActive={hoveredItem === pathComponent.name}
              />
              {pathComponent.name}
            </CdwObjectRowItem>
          </ClickableStyledFormUIRow>
        );
      })}
      {sortedAndFilteredTables.map((cdwTable) => {
        const isSelected = cdwTable.sqlName === currentTable?.sqlName;
        return (
          <ClickableStyledFormUIRow
            key={cdwTable.sqlName}
            onClick={() => onSelectTable(cdwTable)}
            $isHighlighted={isSelected}
            onMouseEnter={() => setHoveredItem(cdwTable.sqlName)}
            onMouseLeave={() => setHoveredItem('')}
          >
            <CdwObjectRowItem>
              <SvgIcon
                width={14}
                height={12}
                name={'table-cdw'}
                isHoverable={true}
                isActive={isSelected || hoveredItem === cdwTable.sqlName}
              />
              {cdwTable.shortName}
            </CdwObjectRowItem>
          </ClickableStyledFormUIRow>
        );
      })}
    </FormUI.Container>
  );
};

export default CdwTableContainer;
